<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import { useRoute } from "vue-router";
import { useTheme } from "@/utils/composables/useTheme";
// import { useCommandPalette } from "@/utils/composables/useCommandPalette";

const authStore = useAuthStore();
const uiStore = useUiStore();
const currentCompany = JSON.parse(localStorage.getItem("currentCompany"));

const toggleSidebar = () => {
  const next = !uiStore.isCollapsed;
  uiStore.updateCollapseState(next);
  localStorage.setItem("sidebarCollapsed", next.toString());
};

const userName = `${
  currentCompany?.pivot?.first_name?.charAt(0).toUpperCase() +
  currentCompany?.pivot?.first_name?.slice(1).toLowerCase()
}  ${
  currentCompany?.pivot?.last_name?.charAt(0).toUpperCase() +
  currentCompany?.pivot?.last_name?.slice(1).toLowerCase()
}`;
const userRole =
  currentCompany?.pivot?.role?.name?.charAt(0).toUpperCase() +
  currentCompany?.pivot?.role?.name?.slice(1).toLowerCase();
const userInitial = currentCompany?.pivot?.first_name?.charAt(0).toUpperCase() || "U";

const isUserMenuOpen = ref(false);
const userMenuWrapper = ref(null);
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};
const handleClickOutside = (event) => {
  if (
    isUserMenuOpen.value &&
    userMenuWrapper.value &&
    !userMenuWrapper.value.contains(event.target)
  ) {
    isUserMenuOpen.value = false;
  }
};
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

const route = useRoute();

const { mode, setTheme } = useTheme();
// Search trigger hidden in the top nav for now — command palette is still
// reachable via its Cmd/Ctrl+K shortcut (see useCommandPalette).
// const { open: openCommandPalette } = useCommandPalette();

// const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
// const shortcutKey = isMac ? "⌘" : "Ctrl";

const themeOptions = [
  { value: "light", label: "Light", icon: "bi bi-sun" },
  { value: "system", label: "System", icon: "bi bi-display" },
  { value: "dark", label: "Dark", icon: "bi bi-moon-stars" },
];

const logout = () => {
  authStore.logout();
};
</script>

<template>
  <header class="top-navbar">
    <div class="navbar-left">
      <button
        class="burger-btn"
        :class="{ 'is-collapsed': uiStore.isCollapsed }"
        @click="toggleSidebar"
        title="Toggle Sidebar"
        :aria-label="uiStore.isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="2.5"/>
          <path d="M9 3v18"/>
          <path class="burger-btn__arrow" d="M16 15l-3-3 3-3"/>
        </svg>
      </button>
      <router-link to="/" class="navbar-logo">
        <img class="navbar-logo-icon" src="@/assets/images/logo/hiddenox.svg" alt="Ravo" />
        <span class="navbar-logo-text">Ravo</span>
      </router-link>
      <h1 class="page-title"></h1>
    </div>
    <div class="navbar-right">
      <div class="actions items-center flex gap-3">
        <!-- <button
          type="button"
          class="cmdk-trigger"
          title="Search (⌘K)"
          @click="openCommandPalette"
        >
          <i class="bi bi-search"></i>
          <span class="cmdk-trigger-text">Search</span>
          <span class="cmdk-trigger-kbd">{{ shortcutKey }}K</span>
        </button> -->

        <div class="company-name" v-if="route.name != 'CompaniesListView'">
          {{ currentCompany.name }}
        </div>

        <span class="navbar-separator" v-if="route.name != 'CompaniesListView'" aria-hidden="true"></span>

        <div
          ref="userMenuWrapper"
          class="user-menu-wrapper"
          v-if="route.name != 'CompaniesListView'"
        >
          <button type="button" class="user-menu-trigger" @click="toggleUserMenu">
            <div class="user-avatar">{{ userInitial }}</div>
            <div class="user-details">
              <span class="user-name">{{ userName }}</span>
              <span class="user-role">{{ userRole }}</span>
            </div>
            <svg class="user-menu-caret" width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3.66772 4.64C3.75812 4.74474 3.87001 4.82877 3.99581 4.88637C4.12161 4.94396 4.25836 4.97377 4.39672 4.97377C4.53508 4.97377 4.6718 4.94396 4.7976 4.88637C4.9234 4.82877 5.03532 4.74474 5.12573 4.64L8.57272 0.809998C8.97272 0.362998 8.81571 0 8.21472 0H0.578698C-0.0213019 0 -0.179273 0.365998 0.220727 0.809998L3.66772 4.64Z" fill="currentColor"/>
            </svg>
          </button>
          <div v-if="isUserMenuOpen" class="user-menu-dropdown">
            <div class="user-menu-row user-menu-row--theme">
              <span class="user-menu-row-label">
                <i class="bi bi-moon"></i>
                Theme
              </span>
              <div class="theme-seg" role="radiogroup" aria-label="Theme">
                <button
                  v-for="opt in themeOptions"
                  :key="opt.value"
                  type="button"
                  class="theme-seg-btn"
                  :class="{ 'is-active': mode === opt.value }"
                  role="radio"
                  :aria-checked="mode === opt.value"
                  :title="opt.label"
                  @click.stop="setTheme(opt.value)"
                >
                  <i :class="opt.icon"></i>
                </button>
              </div>
            </div>
            <div class="user-menu-divider"></div>
            <button type="button" class="user-menu-item logout-action" @click="logout(); isUserMenuOpen = false">
              <i class="bi bi-door-open-fill mr-2"></i>Logout
            </button>
          </div>
        </div>

        <div @click="logout()" v-else>
          <span class="nav-label logout-action">
            <i class="bi bi-door-open-fill"></i><span class="ml-5">Logout</span>
          </span>
        </div>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
/* ── Top navbar — modern style ─────────────────────────────── */
.top-navbar {
  height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 12px;
  background-color: var(--neutral-0);
  border-bottom: 1px solid var(--neutral-200);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  position: relative;
  z-index: 1001;
  flex-shrink: 0;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.burger-btn {
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-500);
  border-radius: 10px;
  padding: 0;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background-color: var(--neutral-100);
    border-color: var(--border-color);
    color: var(--primary-color);
  }

  &:active {
    background-color: var(--border-color);
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
    overflow: visible;
  }

  /* Arrow flips when sidebar is collapsed */
  .burger-btn__arrow {
    transform-origin: 14.5px 12px;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.is-collapsed .burger-btn__arrow {
    transform: rotate(180deg);
  }
}

.navbar-logo {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 8px;
  border-radius: 10px;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--neutral-50);
  }
}

.navbar-logo-icon {
  width: 32px !important;
  height: 32px !important;
  object-fit: contain !important;
  margin-left: 0 !important;
}

.navbar-logo-text {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--neutral-900);
}

.page-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--neutral-900);
  margin: 0;
  letter-spacing: -0.01em;
}

.cmdk-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--neutral-50);
  color: var(--neutral-500);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--neutral-100);
    border-color: var(--neutral-300);
    color: var(--neutral-700);
  }

  @media (max-width: 640px) {
    .cmdk-trigger-text { display: none; }
  }
}

.cmdk-trigger-kbd {
  font-size: 11px;
  font-weight: 600;
  color: var(--neutral-400);
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 5px;
  padding: 1px 5px;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.company-name {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--neutral-100);
  border: 1px solid var(--border-color);
  color: var(--neutral-800);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    display: none;
  }
}

.actions {
  display: flex;
  gap: var(--spacing-3);
}

.navbar-separator {
  display: inline-block;
  width: 1px;
  height: 24px;
  background-color: var(--border-color);
  margin: 0 4px;

  @media (max-width: 640px) {
    display: none;
  }
}

.user-menu-wrapper {
  position: relative;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 4px 10px 4px 4px;
  cursor: pointer;
  color: var(--neutral-600);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background-color: var(--neutral-50);
    border-color: var(--border-color);
  }

  &[aria-expanded="true"],
  &:focus-visible {
    outline: none;
    background-color: var(--neutral-100);
    border-color: var(--neutral-300);
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #e0ecff 0%, #c7d2fe 100%);
    color: var(--primary-hover);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
    flex-shrink: 0;
    box-shadow: inset 0 0 0 1px rgba(47, 77, 132, 0.18);
  }

  .user-details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.15;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .user-name {
    font-weight: 600;
    color: var(--neutral-900);
    font-size: 0.8125rem;
  }

  .user-role {
    font-size: 0.6875rem;
    color: var(--neutral-400);
    text-transform: capitalize;
  }

  .user-menu-caret {
    color: var(--neutral-400);
    margin-left: 2px;
    transition: transform 0.2s ease, color 0.15s ease;
  }

  &:hover .user-menu-caret { color: var(--neutral-600); }
}

.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 256px;
  background-color: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 14px;
  box-shadow:
    0 12px 32px -8px rgba(15, 23, 42, 0.18),
    0 4px 12px -4px rgba(15, 23, 42, 0.08);
  z-index: 1100;
  overflow: hidden;
  padding: 6px;
  animation: navbar-menu-slide 0.18s ease-out;
}

@keyframes navbar-menu-slide {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.user-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 9px 12px;
  background: none;
  border: none;
  border-radius: 10px;
  color: var(--neutral-700);
  text-align: left;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: background-color 0.15s ease, color 0.15s ease;

  &.logout-action {
    color: #ef4444;

    i { color: #ef4444; }

    &:hover {
      background-color: #fef2f2;
      color: #dc2626;
      i { color: #dc2626; }
    }
  }

  &:hover {
    background-color: var(--neutral-100);
    color: var(--neutral-900);
  }

  i {
    margin-right: 10px;
    font-size: 0.95rem;
    width: 18px;
    text-align: center;
  }
}

.user-menu-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--neutral-700);
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: var(--neutral-100);
    color: var(--neutral-900);
  }

  &.user-menu-row--theme {
    cursor: default;

    &:hover {
      background-color: transparent;
      color: var(--neutral-700);
    }
  }
}

.user-menu-row-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8125rem;

  i {
    font-size: 0.95rem;
    color: var(--neutral-500);
    width: 18px;
    text-align: center;
  }
}

.user-menu-divider {
  height: 1px;
  background-color: var(--neutral-200);
  margin: 6px 4px;
}

.theme-seg {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background-color: var(--neutral-100);
  border-radius: 999px;
  flex-shrink: 0;
}

.theme-seg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--neutral-400);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  i { font-size: 0.8rem; }

  &:hover {
    color: var(--neutral-700);
  }

  &.is-active {
    background-color: var(--neutral-0);
    color: var(--primary-color);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
  }
}

</style>

<style lang="scss">
/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .top-navbar .cmdk-trigger {
  background-color: #1a2335;
  border-color: #2a3552;
  color: #9aadc4;

  &:hover {
    background-color: #243049;
    border-color: #3a4665;
    color: #e8edf5;
  }
}

[data-theme="dark"] .top-navbar .cmdk-trigger-kbd {
  background-color: #131a2b;
  border-color: #2a3552;
  color: #9aadc4;
}

[data-theme="dark"] .top-navbar {
  background-color: #131a2b !important;
  border-bottom-color: #2a3552 !important;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25) !important;
}

[data-theme="dark"] .top-navbar .navbar-logo {
  &:hover { background-color: var(--neutral-200); }
}

[data-theme="dark"] .top-navbar .page-title {
  color: #e8edf5;
}

[data-theme="dark"] .top-navbar .company-name {
  background: rgba(78, 127, 255, 0.1);
  border-color: #2d3f5a;
  color: #c5d3ec;
}

[data-theme="dark"] .top-navbar .user-menu-trigger {
  color: #e8edf5;

  &:hover {
    background-color: #1f2a44;
    border-color: #2a3552;
  }

  &[aria-expanded="true"],
  &:focus-visible {
    background-color: #243049;
    border-color: #3a4665;
  }

  .user-name { color: #e8edf5; }
  .user-role { color: #9aadc4; }
  .user-menu-caret { color: #9aadc4; }
  .user-avatar {
    background: linear-gradient(135deg, #1a2d52 0%, #28364e 100%);
    color: #a8c4ff;
    box-shadow: inset 0 0 0 1px rgba(107, 150, 255, 0.25);
  }

  &:hover .user-menu-caret { color: #c5d3ec; }
}

[data-theme="dark"] .top-navbar .user-menu-dropdown {
  background-color: #1a2335;
  border-color: #2a3552;
  box-shadow:
    0 12px 32px -8px rgba(0, 0, 0, 0.65),
    0 4px 12px -4px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .top-navbar .user-menu-row {
  color: #e8edf5;

  &:hover {
    background-color: #243049;
    color: #ffffff;
  }

  i { color: #9aadc4; }
}

[data-theme="dark"] .top-navbar .user-menu-divider,
[data-theme="dark"] .top-navbar .navbar-separator {
  background-color: #2a3552;
}

[data-theme="dark"] .top-navbar .user-menu-item {
  color: #e8edf5;

  &:hover {
    background-color: #243049;
    color: #ffffff;
  }

  &.logout-action {
    color: #f87171;

    i { color: #f87171; }

    &:hover {
      background-color: rgba(248, 113, 113, 0.18);
      color: #fca5a5;
      i { color: #fca5a5; }
    }
  }
}

[data-theme="dark"] .top-navbar .theme-seg {
  background-color: #243049;
}

[data-theme="dark"] .top-navbar .theme-seg-btn {
  color: #9aadc4;

  &:hover { color: #e8edf5; }

  &.is-active {
    background-color: #1a2335;
    color: #6b96ff;
  }
}

[data-theme="dark"] .top-navbar .burger-btn {
  color: #9aadc4;
  border-color: transparent;

  &:hover {
    background-color: #243049;
    border-color: #2a3552;
    color: #6b96ff;
  }

  &:active { background-color: #1f2a44; }
}
</style>
